import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["as", "autoAlign", "children", "hasIconOnly", "tooltipHighContrast", "tooltipDropShadow", "iconDescription", "kind", "onBlur", "onClick", "onFocus", "onMouseEnter", "onMouseLeave", "renderIcon", "size", "tooltipAlignment", "tooltipPosition"],
  _excluded2 = ["tooltipAlignment"];
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import { IconButton, IconButtonKinds } from '../IconButton/index.js';
import ButtonBase from './ButtonBase.js';
var ButtonKinds = ['primary', 'secondary', 'danger', 'ghost', 'danger--primary', 'danger--ghost', 'danger--tertiary', 'tertiary'];
var ButtonSizes = ['sm', 'md', 'lg', 'xl', '2xl'];
var ButtonTooltipAlignments = ['start', 'center', 'end'];
var ButtonTooltipPositions = ['top', 'right', 'bottom', 'left'];
function isIconOnlyButton(hasIconOnly, _kind) {
  if (hasIconOnly === true) {
    return true;
  }
  return false;
}
var Button = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var as = props.as,
    _props$autoAlign = props.autoAlign,
    autoAlign = _props$autoAlign === void 0 ? false : _props$autoAlign,
    children = props.children,
    _props$hasIconOnly = props.hasIconOnly,
    hasIconOnly = _props$hasIconOnly === void 0 ? false : _props$hasIconOnly,
    _props$tooltipHighCon = props.tooltipHighContrast,
    tooltipHighContrast = _props$tooltipHighCon === void 0 ? true : _props$tooltipHighCon,
    _props$tooltipDropSha = props.tooltipDropShadow,
    tooltipDropShadow = _props$tooltipDropSha === void 0 ? false : _props$tooltipDropSha,
    iconDescription = props.iconDescription,
    _props$kind = props.kind,
    kind = _props$kind === void 0 ? 'primary' : _props$kind,
    onBlur = props.onBlur,
    onClick = props.onClick,
    onFocus = props.onFocus,
    onMouseEnter = props.onMouseEnter,
    onMouseLeave = props.onMouseLeave,
    ButtonImageElement = props.renderIcon,
    size = props.size,
    _props$tooltipAlignme = props.tooltipAlignment,
    tooltipAlignment = _props$tooltipAlignme === void 0 ? 'center' : _props$tooltipAlignme,
    _props$tooltipPositio = props.tooltipPosition,
    tooltipPosition = _props$tooltipPositio === void 0 ? 'top' : _props$tooltipPositio,
    rest = _objectWithoutProperties(props, _excluded);
  if (ButtonImageElement && !children && !iconDescription) {
    console.error('Button: renderIcon property specified without also providing an iconDescription property. ' + 'This may impact accessibility for screen reader users.');
  }
  var iconOnlyImage = !ButtonImageElement ? null : /*#__PURE__*/React.createElement(ButtonImageElement, null);
  if (!isIconOnlyButton(hasIconOnly)) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    var _tooltipAlignment = props.tooltipAlignment,
      propsWithoutTooltipAlignment = _objectWithoutProperties(props, _excluded2);
    return /*#__PURE__*/React.createElement(ButtonBase, _extends({
      ref: ref
    }, propsWithoutTooltipAlignment));
  } else {
    var align = undefined;
    if (tooltipPosition === 'top' || tooltipPosition === 'bottom') {
      if (tooltipAlignment === 'center') {
        align = tooltipPosition;
      }
      if (tooltipAlignment === 'end') {
        align = "".concat(tooltipPosition, "-end");
      }
      if (tooltipAlignment === 'start') {
        align = "".concat(tooltipPosition, "-start");
      }
    }
    if (tooltipPosition === 'right' || tooltipPosition === 'left') {
      align = tooltipPosition;
    }
    return (/*#__PURE__*/
      // @ts-expect-error - `IconButton` does not support all `size`s that
      // `Button` supports.
      //
      // TODO: What should be done here?
      // 1. Should the `IconButton` not be rendered if the `size` is not
      //    supported?
      // 2. Should an error be thrown?
      // 3. Something else?
      React.createElement(IconButton, _extends({}, rest, {
        ref: ref,
        as: as,
        align: align,
        label: iconDescription,
        kind: kind,
        size: size,
        highContrast: tooltipHighContrast,
        dropShadow: tooltipDropShadow,
        onMouseEnter: onMouseEnter,
        onMouseLeave: onMouseLeave,
        onFocus: onFocus,
        onBlur: onBlur,
        autoAlign: autoAlign,
        onClick: onClick,
        renderIcon: iconOnlyImage ? null : ButtonImageElement // avoid doubling the icon.
      }), iconOnlyImage !== null && iconOnlyImage !== void 0 ? iconOnlyImage : children)
    );
  }
});
Button.displayName = 'Button';
export { ButtonKinds, ButtonSizes, ButtonTooltipAlignments, ButtonTooltipPositions, Button as default };